// @flow
import * as React from 'react';
import idleTimeout from 'idle-timeout';

import useTranslations from 'custom-hooks/use-translations';
import Modal from 'ui/design-system/modal';
import type { Props as ModalProps } from 'ui/design-system/modal';

export type Props = $ReadOnly<{|
  documentDirty: () => boolean,
  saveDocument: () => Promise<void>,
  scrambleBuildRev: string,
  deployedScrambleBuildRev: () => Promise<string>,
  versionCheckInterval?: number,
  userInteractionTimeout?: number,
  ...ModalProps,
|}>;

const HOUR = 60 * 60 * 1000;
const FIVE_SECONDS = 5 * 1000;

const periodically = (cb: (next: () => void) => void, interval: number) => {
  const handle: {| current?: TimeoutID |} = { ...null };
  const next = () => {
    // eslint-disable-next-line fp/no-mutation
    handle.current = setTimeout(() => cb(next), interval);
  };

  // eslint-disable-next-line fp/no-mutation
  handle.current = setTimeout(() => cb(next), interval);
  return () => clearTimeout(handle.current);
};

const VersionChangeModal = ({
  appElement,
  documentDirty,
  saveDocument,
  scrambleBuildRev,
  deployedScrambleBuildRev,
  versionCheckInterval = HOUR,
  userInteractionTimeout = FIVE_SECONDS,
}: Props): React.Node => {
  const { translate } = useTranslations();
  const [isOpen, setIsOpen] = React.useState(false);

  const periodicVersionCheck = React.useCallback(
    (next: () => void) => {
      deployedScrambleBuildRev()
        .then((rev) => {
          const revChanged = rev !== scrambleBuildRev;
          idleTimeout(
            () => {
              setIsOpen(revChanged);
              if (!revChanged) next();
            },
            { timeout: userInteractionTimeout },
          );
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('error getting scramble build rev', error);
          next();
        });
    },
    [deployedScrambleBuildRev, scrambleBuildRev, userInteractionTimeout],
  );

  const snoozeVersionCheck = React.useCallback(() => {
    setTimeout(() => {
      deployedScrambleBuildRev().then((rev) => {
        idleTimeout(() => setIsOpen(rev !== scrambleBuildRev), {
          timeout: userInteractionTimeout,
        });
      });
    }, versionCheckInterval);
  }, [
    deployedScrambleBuildRev,
    scrambleBuildRev,
    userInteractionTimeout,
    versionCheckInterval,
  ]);

  React.useEffect(
    // periodically returns a cleanup function so unintended periodic checks are avoided
    () => periodically(periodicVersionCheck, versionCheckInterval),
    [periodicVersionCheck, versionCheckInterval],
  );

  const handleSnooze = React.useCallback(() => {
    snoozeVersionCheck();
    setIsOpen(false);
  }, [snoozeVersionCheck]);

  const [dirtyActions, actions] = React.useMemo(() => {
    const snoozeAction = {
      handleClick: handleSnooze,
      label: translate('new_version_snooze'),
      type: 'secondary',
    };
    return [
      [
        snoozeAction,
        {
          handleClick: () => {
            saveDocument().then(() => window.location.reload());
          },
          type: 'primary',
          label: translate('new_version_save_and_reload'),
        },
      ],
      [
        snoozeAction,
        {
          handleClick: () => window.location.reload(),
          label: translate('new_version_reload'),
          type: 'primary',
        },
      ],
    ];
  }, [handleSnooze, saveDocument, translate]);
  return (
    <Modal
      isOpen={isOpen}
      appElement={appElement}
      title={translate('new_version_available_modal')}
      contentLabel={translate('new_version_available_message')}
      actions={documentDirty() ? dirtyActions : actions}
      onRequestClose={handleSnooze}
    >
      {translate('new_version_available_message')}
    </Modal>
  );
};

export default VersionChangeModal;
